import { ChevronRightIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/core/button';
import { Card } from '@/components/core/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/core/collapsible';
import { cn } from '@/shared/helpers';

type CollapsibleCardRootProps = Pick<
  React.ComponentProps<typeof Collapsible>,
  'defaultOpen' | 'open' | 'onOpenChange' | 'disabled'
>;

type CollapsibleCardProps = React.ComponentPropsWithoutRef<typeof Card> & CollapsibleCardRootProps;

const CollapsibleCard = React.forwardRef<React.ElementRef<typeof Card>, CollapsibleCardProps>(
  ({ open, defaultOpen, onOpenChange, disabled, className, ...props }, ref) => {
    return (
      <Collapsible
        asChild
        data-slot="card"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        disabled={disabled}
      >
        <Card
          ref={ref}
          className={cn('[&[data-state=closed]>[data-slot=card-header]]:border-b-0', className)}
          {...props}
        />
      </Collapsible>
    );
  },
);
CollapsibleCard.displayName = 'CollapsibleCard';

type CollapsibleCardTriggerProps = Omit<
  React.ComponentProps<typeof Button>,
  'asChild' | 'children' | 'size' | 'variant'
>;

function CollapsibleCardTrigger({ className, ...props }: CollapsibleCardTriggerProps) {
  return (
    <CollapsibleTrigger asChild>
      <Button
        {...props}
        type="button"
        variant="ghost"
        size="icon-sm"
        className={cn(
          'group/collapsible-card-trigger last:-mr-1 data-[variant=ghost]:bg-card data-[variant=ghost]:hover:bg-accent',
          className,
        )}
      >
        <ChevronRightIcon
          aria-hidden="true"
          className="size-5 transition-transform duration-200 group-data-[state=open]/collapsible-card-trigger:rotate-90"
        />
      </Button>
    </CollapsibleTrigger>
  );
}

function CollapsibleCardContent({
  className,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  return <CollapsibleContent className={cn('overflow-hidden', className)} {...props} />;
}

export { CollapsibleCard, CollapsibleCardContent, CollapsibleCardTrigger };
